import { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { RateUnitEnum } from "@platform/openapi-types";

import { rateFieldPathBuilder } from "../../../utils/rateFieldPath";
import {
  useResolvedRateSchedules,
  useUpdateRate,
  useUpdateProposalRate,
} from "../../../hooks/useRates";
import { formatRateValue } from "../../../utils/rateFormatters";
import {
  getRenewalColumns,
  primarySectionSchedules,
  slotKeyWithUnit,
  type RenewalColumns,
} from "../../../utils/rateSets";

import { DebouncedNumericInput, NumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  RenewalGroupHeaderCell,
  RenewalHeaderCell,
  RenewalRateCell,
  RenewalRowCells,
  RenewalSubHeaderCells,
} from "./RenewalRateCell";
import {
  FONT_SIZE,
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  inputSx,
  readOnlyInputSx,
  gridRowSx,
  gridCellSx,
  headerCellSx,
} from "./constants";

import type { AxiosInstance } from "axios";
import type { RateFieldPathBuilder } from "../../../utils/rateFieldPath";
import type { ColDef } from "ag-grid-community";
import type { FC, JSX } from "react";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";
import type { Rate } from "../../../types";

export interface FmlaPepmRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type (e.g., fmla) for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
}

/**
 * Build column definitions for FMLA PEPM rate design in RatesGrid
 */
export const buildFmlaPepmColumns = (_group: BenefitPlanGroup): ColDef[] =>
  // FMLA PEPM doesn't need additional sub-columns within the group column
  // All data is shown in a single cell
  [];

/**
 * Build column definitions for FMLA PEPM rate design in SummaryGrid
 * Returns array of ColDef for sub-columns within a benefit plan
 */
export const buildFmlaPepmSummaryColumns = (
  _planId: string,
  _planName: string,
): ColDef[] =>
  // TODO: Implement FMLA PEPM summary column generation
  [];

// Local grid layouts
// CSS Grid container styling - 2 columns: Label, Value; a renewal column is appended when
// renewal is shown and the table scrolls within the pinned benefit-plan cell.
const singleGridSx = (showRenewal: boolean): Record<string, unknown> =>
  showRenewal
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(100px, 1fr) repeat(2, minmax(80px, 1fr))",
        width: "100%",
        overflowX: "auto",
      }
    : { display: "grid", gridTemplateColumns: "1fr 1fr", width: "100%" };

// Gender split - Label, Male, Female, plus a renewal column per gender when shown.
const genderGridSx = (showRenewal: boolean): Record<string, unknown> => ({
  display: "grid",
  gridTemplateColumns: `minmax(100px, 1fr) repeat(${
    showRenewal ? 4 : 2
  }, minmax(80px, 1fr))`,
  width: "100%",
  ...(showRenewal ? { overflowX: "auto" } : {}),
});

/** Sum of the displayed renewal values for two rate slots (renewal TOTAL PEPM). */
const renewalTotalOf = (
  renewal: RenewalColumns,
  ...componentRates: (Rate | undefined)[]
): number =>
  componentRates.reduce(
    (sum, rate) =>
      sum + (rate ? (renewal.columnBySlot[slotKeyWithUnit(rate)] ?? 0) : 0),
    0,
  );

/** Parameters for rate update mutations */
interface FmlaRateUpdateParams {
  scheduleId: string;
  rateId: string;
  benefitPlanId: string;
  rate: { rate?: number; numberOfLives?: number };
}

/** Context for creating rate handlers */
interface FmlaHandlerContext {
  axiosInstance: AxiosInstance | undefined;
  scheduleId: string | undefined;
  planId: string;
  mutate: (params: FmlaRateUpdateParams) => void;
}

/** Create a handler for saving numberOfLives */
const createLivesHandler =
  (
    ctx: FmlaHandlerContext,
    rate: Rate | undefined,
  ): ((value: string) => void) =>
  (value: string): void => {
    if (!ctx.axiosInstance || !ctx.scheduleId || !rate?.id) return;
    const numericValue = parseInt(value, 10);
    if (isNaN(numericValue)) return;
    ctx.mutate({
      scheduleId: ctx.scheduleId,
      rateId: rate.id,
      benefitPlanId: ctx.planId,
      rate: { numberOfLives: numericValue },
    });
  };

/** Create a handler for saving rate with total update */
const createFmlaRateHandler =
  (
    ctx: FmlaHandlerContext,
    rate: Rate | undefined,
    otherRate: Rate | undefined,
    totalRate: Rate | undefined,
  ): ((value: string) => void) =>
  (value: string): void => {
    if (!ctx.axiosInstance || !ctx.scheduleId || !rate?.id) return;
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return;
    // Update the rate
    ctx.mutate({
      scheduleId: ctx.scheduleId,
      rateId: rate.id,
      benefitPlanId: ctx.planId,
      rate: { rate: numericValue },
    });
    // Update total if we have a total rate
    if (totalRate?.id) {
      const otherValue = otherRate?.rate ?? 0;
      ctx.mutate({
        scheduleId: ctx.scheduleId,
        rateId: totalRate.id,
        benefitPlanId: ctx.planId,
        rate: { rate: numericValue + otherValue },
      });
    }
  };

/** Rates for a single column (gender) of FMLA PEPM */
interface FmlaPepmColumnRates {
  fmlaRate: Rate | undefined;
  adaRate: Rate | undefined;
  totalRate: Rate | undefined;
}

/** Props for single column FMLA layout */
interface FmlaSingleColumnProps {
  planId: string;
  fmlaRate: Rate | undefined;
  adaRate: Rate | undefined;
  totalPepm: number;
  rateScheduleNumberOfLives: number | undefined;
  onW2EmployeesCommit: (value: string) => void;
  onFmlaPepmCommit: (value: string) => void;
  onAdaPepmCommit: (value: string) => void;
  readOnly?: boolean;
  renewal: RenewalColumns;
  fieldPathOf: RateFieldPathBuilder;
}

/** Props for gender split FMLA layout */
interface FmlaGenderSplitProps {
  planId: string;
  maleRates: FmlaPepmColumnRates;
  femaleRates: FmlaPepmColumnRates;
  maleTotalPepm: number;
  femaleTotalPepm: number;
  onMaleW2Commit: (value: string) => void;
  onMaleFmlaCommit: (value: string) => void;
  onMaleAdaCommit: (value: string) => void;
  onFemaleW2Commit: (value: string) => void;
  onFemaleFmlaCommit: (value: string) => void;
  onFemaleAdaCommit: (value: string) => void;
  readOnly?: boolean;
  renewal: RenewalColumns;
  fieldPathOf: RateFieldPathBuilder;
}

/** Single column FMLA PEPM layout component */
const FmlaSingleColumn: FC<FmlaSingleColumnProps> = ({
  planId,
  fmlaRate,
  adaRate,
  totalPepm,
  rateScheduleNumberOfLives,
  onW2EmployeesCommit,
  onFmlaPepmCommit,
  onAdaPepmCommit,
  readOnly,
  renewal,
  fieldPathOf,
}): JSX.Element => {
  const formattedTotalPepm = totalPepm.toFixed(2);
  const showRenewal = renewal.showRenewal;
  const currentEndBorderRight = showRenewal ? AG_BORDER : "none";

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Rate Design Label Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "36px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>FMLA PEPM</Box>
      </Box>

      <Box sx={singleGridSx(showRenewal)}>
        {/* Column header row — only needed to name Current vs Renewal */}
        {showRenewal && (
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }} />
            <Box sx={{ ...headerCellSx, justifyContent: "flex-end" }}>
              Current
            </Box>
            <RenewalHeaderCell show={showRenewal} label={renewal.columnLabel} />
          </Box>
        )}

        {/* Number of W2 Employees Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>Number of W2 Employees</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(fmlaRate, "numberOfLives")}
            sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
          >
            <DebouncedNumericInput
              serverValue={
                fmlaRate?.numberOfLives != null
                  ? String(fmlaRate.numberOfLives)
                  : rateScheduleNumberOfLives != null
                    ? String(rateScheduleNumberOfLives)
                    : ""
              }
              onCommit={onW2EmployeesCommit}
              allowDecimals={false}
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={0}
              columnsInRow={1}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Number of W2 Employees" }}
            />
          </RateReviewCell>
          {showRenewal && <Box sx={{ borderLeft: AG_BORDER }} />}
        </Box>

        {/* FMLA PEPM Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>FMLA PEPM</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(fmlaRate, "rate")}
            sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(fmlaRate?.rate, fmlaRate?.unit)}
              onCommit={onFmlaPepmCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={0}
              columnsInRow={1}
              readOnly={readOnly}
              inputProps={{ "aria-label": "FMLA PEPM" }}
            />
          </RateReviewCell>
          {showRenewal && fmlaRate && (
            <RenewalRateCell
              slotKey={slotKeyWithUnit(fmlaRate)}
              renewal={renewal}
              rowLabel="FMLA PEPM"
              isLastRow={false}
            />
          )}
        </Box>

        {/* ADA PEPM Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>ADA PEPM</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(adaRate, "rate")}
            sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(adaRate?.rate, adaRate?.unit)}
              onCommit={onAdaPepmCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={0}
              columnsInRow={1}
              readOnly={readOnly}
              inputProps={{ "aria-label": "ADA PEPM" }}
            />
          </RateReviewCell>
          {showRenewal && adaRate && (
            <RenewalRateCell
              slotKey={slotKeyWithUnit(adaRate)}
              renewal={renewal}
              rowLabel="ADA PEPM"
              isLastRow={false}
            />
          )}
        </Box>

        {/* Total PEPM Row (calculated, read-only) */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx, borderBottom: "none" }}>TOTAL PEPM</Box>
          <Box
            sx={{
              ...gridCellSx,
              borderRight: currentEndBorderRight,
              borderBottom: "none",
            }}
          >
            <NumericInput
              value={formattedTotalPepm}
              onChange={(): void => {}}
              readOnly
              isCurrency
              sx={readOnlyInputSx}
              inputProps={{ "aria-label": "Total PEPM" }}
            />
          </Box>
          {showRenewal && (
            <Box
              sx={{
                ...gridCellSx,
                borderLeft: AG_BORDER,
                borderRight: "none",
                borderBottom: "none",
              }}
            >
              <NumericInput
                value={renewalTotalOf(renewal, fmlaRate, adaRate).toFixed(2)}
                onChange={(): void => {}}
                readOnly
                isCurrency
                sx={readOnlyInputSx}
                inputProps={{
                  "aria-label": `${renewal.columnLabel} Total PEPM`,
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/** Gender split FMLA PEPM layout component */
const FmlaGenderSplit: FC<FmlaGenderSplitProps> = ({
  planId,
  maleRates,
  femaleRates,
  maleTotalPepm,
  femaleTotalPepm,
  onMaleW2Commit,
  onMaleFmlaCommit,
  onMaleAdaCommit,
  onFemaleW2Commit,
  onFemaleFmlaCommit,
  onFemaleAdaCommit,
  readOnly,
  renewal,
  fieldPathOf,
}): JSX.Element => {
  const showRenewal = renewal.showRenewal;
  const currentEndBorderRight = showRenewal ? AG_BORDER : "none";
  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Rate Design Label Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "36px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>FMLA PEPM</Box>
      </Box>

      <Box sx={genderGridSx(showRenewal)}>
        {/* Group header row — only when renewal columns are present */}
        {showRenewal && (
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }} />
            <Box
              sx={{
                ...headerCellSx,
                gridColumn: "span 2",
                justifyContent: "center",
                fontWeight: 600,
                borderRight: AG_BORDER,
              }}
            >
              Current
            </Box>
            <RenewalGroupHeaderCell
              show={showRenewal}
              label={renewal.columnLabel}
              span={2}
            />
          </Box>
        )}

        {/* Column Headers: Label | Male | Female */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}></Box>
          <Box
            sx={{ ...headerCellSx, justifyContent: "center", fontWeight: 600 }}
          >
            Male
          </Box>
          <Box
            sx={{
              ...headerCellSx,
              justifyContent: "center",
              fontWeight: 600,
              borderRight: currentEndBorderRight,
            }}
          >
            Female
          </Box>
          <RenewalSubHeaderCells
            show={showRenewal}
            labels={["Male", "Female"]}
          />
        </Box>

        {/* Number of W2 Employees Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>Number of W2 Employees</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(maleRates.fmlaRate, "numberOfLives")}
            sx={{ ...gridCellSx }}
          >
            <DebouncedNumericInput
              serverValue={
                maleRates.fmlaRate?.numberOfLives != null
                  ? String(maleRates.fmlaRate.numberOfLives)
                  : ""
              }
              onCommit={onMaleW2Commit}
              allowDecimals={false}
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={0}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Male Number of W2 Employees" }}
            />
          </RateReviewCell>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(femaleRates.fmlaRate, "numberOfLives")}
            sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
          >
            <DebouncedNumericInput
              serverValue={
                femaleRates.fmlaRate?.numberOfLives != null
                  ? String(femaleRates.fmlaRate.numberOfLives)
                  : ""
              }
              onCommit={onFemaleW2Commit}
              allowDecimals={false}
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={1}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Female Number of W2 Employees" }}
            />
          </RateReviewCell>
          <RenewalRowCells
            show={showRenewal}
            renewal={renewal}
            rowLabelPrefix=""
            keyOf={slotKeyWithUnit}
            isLastRow={false}
            cells={[
              { rate: undefined, label: "male-w2" },
              { rate: undefined, label: "female-w2" },
            ]}
          />
        </Box>

        {/* FMLA PEPM Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>FMLA PEPM</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(maleRates.fmlaRate, "rate")}
            sx={{ ...gridCellSx }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                maleRates.fmlaRate?.rate,
                maleRates.fmlaRate?.unit,
              )}
              onCommit={onMaleFmlaCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={0}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Male FMLA PEPM" }}
            />
          </RateReviewCell>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(femaleRates.fmlaRate, "rate")}
            sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                femaleRates.fmlaRate?.rate,
                femaleRates.fmlaRate?.unit,
              )}
              onCommit={onFemaleFmlaCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={1}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Female FMLA PEPM" }}
            />
          </RateReviewCell>
          <RenewalRowCells
            show={showRenewal}
            renewal={renewal}
            rowLabelPrefix=""
            keyOf={slotKeyWithUnit}
            isLastRow={false}
            cells={[
              { rate: maleRates.fmlaRate, label: "Male FMLA PEPM" },
              { rate: femaleRates.fmlaRate, label: "Female FMLA PEPM" },
            ]}
          />
        </Box>

        {/* ADA PEPM Row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>ADA PEPM</Box>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(maleRates.adaRate, "rate")}
            sx={{ ...gridCellSx }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                maleRates.adaRate?.rate,
                maleRates.adaRate?.unit,
              )}
              onCommit={onMaleAdaCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={0}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Male ADA PEPM" }}
            />
          </RateReviewCell>
          <RateReviewCell
            planId={planId}
            fieldPath={fieldPathOf(femaleRates.adaRate, "rate")}
            sx={{ ...gridCellSx, borderRight: currentEndBorderRight }}
          >
            <DebouncedNumericInput
              serverValue={formatRateValue(
                femaleRates.adaRate?.rate,
                femaleRates.adaRate?.unit,
              )}
              onCommit={onFemaleAdaCommit}
              allowDecimals
              isCurrency
              sx={inputSx}
              gridId={`fmla-pepm-${planId}`}
              cellIndex={1}
              columnsInRow={2}
              readOnly={readOnly}
              inputProps={{ "aria-label": "Female ADA PEPM" }}
            />
          </RateReviewCell>
          <RenewalRowCells
            show={showRenewal}
            renewal={renewal}
            rowLabelPrefix=""
            keyOf={slotKeyWithUnit}
            isLastRow={false}
            cells={[
              { rate: maleRates.adaRate, label: "Male ADA PEPM" },
              { rate: femaleRates.adaRate, label: "Female ADA PEPM" },
            ]}
          />
        </Box>

        {/* Total PEPM Row (calculated, read-only) */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx, borderBottom: "none" }}>TOTAL PEPM</Box>
          <Box sx={{ ...gridCellSx, borderBottom: "none" }}>
            <NumericInput
              value={maleTotalPepm.toFixed(2)}
              onChange={(): void => {}}
              readOnly
              isCurrency
              sx={readOnlyInputSx}
              inputProps={{ "aria-label": "Male Total PEPM" }}
            />
          </Box>
          <Box
            sx={{
              ...gridCellSx,
              borderRight: currentEndBorderRight,
              borderBottom: "none",
            }}
          >
            <NumericInput
              value={femaleTotalPepm.toFixed(2)}
              onChange={(): void => {}}
              readOnly
              isCurrency
              sx={readOnlyInputSx}
              inputProps={{ "aria-label": "Female Total PEPM" }}
            />
          </Box>
          {showRenewal && (
            <>
              <Box
                sx={{
                  ...gridCellSx,
                  borderLeft: AG_BORDER,
                  borderBottom: "none",
                }}
              >
                <NumericInput
                  value={renewalTotalOf(
                    renewal,
                    maleRates.fmlaRate,
                    maleRates.adaRate,
                  ).toFixed(2)}
                  onChange={(): void => {}}
                  readOnly
                  isCurrency
                  sx={readOnlyInputSx}
                  inputProps={{
                    "aria-label": `Male ${renewal.columnLabel} Total`,
                  }}
                />
              </Box>
              <Box
                sx={{
                  ...gridCellSx,
                  borderLeft: AG_BORDER,
                  borderRight: "none",
                  borderBottom: "none",
                }}
              >
                <NumericInput
                  value={renewalTotalOf(
                    renewal,
                    femaleRates.fmlaRate,
                    femaleRates.adaRate,
                  ).toFixed(2)}
                  onChange={(): void => {}}
                  readOnly
                  isCurrency
                  sx={readOnlyInputSx}
                  inputProps={{
                    "aria-label": `Female ${renewal.columnLabel} Total`,
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Renderer for FMLA PEPM rate design
 *
 * Displays:
 * - Number of W2 Employees (enrolled lives)
 * - FMLA PEPM (editable rate)
 * - ADA PEPM (editable rate)
 * - TOTAL PEPM (calculated: FMLA PEPM + ADA PEPM, read-only)
 *
 * When gender split is detected (rates have gender property):
 * - Shows Male and Female columns side by side
 * - Each gender has its own W2 Employees, FMLA PEPM, ADA PEPM, and TOTAL PEPM
 *
 * Updates rate on blur via PATCH API
 */
export const FmlaPepmRenderer: FC<FmlaPepmRendererProps> = ({
  plan,
  axiosInstance,
  onRateSaved,
  readOnly,
}) => {
  const { data: rateSchedules, isLoading } = useResolvedRateSchedules(
    plan,
    axiosInstance,
  );

  const rateSchedule = rateSchedules?.[0];

  // Memoize rates to prevent dependency issues in downstream useMemo hooks
  const rates = useMemo(() => rateSchedule?.rates || [], [rateSchedule?.rates]);
  const fieldPathOf = rateFieldPathBuilder(rateSchedule);

  const renewal = useMemo(
    () =>
      getRenewalColumns(
        primarySectionSchedules(rateSchedules),
        slotKeyWithUnit,
      ),
    [rateSchedules],
  );

  // Detect if we have gender-split rates
  const hasGender = useMemo(() => rates.some((r) => r.gender != null), [rates]);

  // Find rates by unit type (non-gender)
  const { fmlaRate, adaRate, totalRate } = useMemo(() => {
    if (hasGender) {
      return { fmlaRate: undefined, adaRate: undefined, totalRate: undefined };
    }
    return {
      fmlaRate: rates.find((r) => r.unit === RateUnitEnum.fmlaPepm),
      adaRate: rates.find((r) => r.unit === RateUnitEnum.adaPepm),
      totalRate: rates.find((r) => r.unit === RateUnitEnum.totalPepm),
    };
  }, [rates, hasGender]);

  // Find gender-split rates
  const { maleRates, femaleRates } = useMemo((): {
    maleRates: FmlaPepmColumnRates;
    femaleRates: FmlaPepmColumnRates;
  } => {
    if (!hasGender) {
      return {
        maleRates: {
          fmlaRate: undefined,
          adaRate: undefined,
          totalRate: undefined,
        },
        femaleRates: {
          fmlaRate: undefined,
          adaRate: undefined,
          totalRate: undefined,
        },
      };
    }
    return {
      maleRates: {
        fmlaRate: rates.find(
          (r) => r.unit === RateUnitEnum.fmlaPepm && r.gender === "male",
        ),
        adaRate: rates.find(
          (r) => r.unit === RateUnitEnum.adaPepm && r.gender === "male",
        ),
        totalRate: rates.find(
          (r) => r.unit === RateUnitEnum.totalPepm && r.gender === "male",
        ),
      },
      femaleRates: {
        fmlaRate: rates.find(
          (r) => r.unit === RateUnitEnum.fmlaPepm && r.gender === "female",
        ),
        adaRate: rates.find(
          (r) => r.unit === RateUnitEnum.adaPepm && r.gender === "female",
        ),
        totalRate: rates.find(
          (r) => r.unit === RateUnitEnum.totalPepm && r.gender === "female",
        ),
      },
    };
  }, [rates, hasGender]);

  // Calculate total PEPM (single column)
  const totalPepm = useMemo(() => {
    const fmla = fmlaRate?.rate ?? 0;
    const ada = adaRate?.rate ?? 0;
    return fmla + ada;
  }, [fmlaRate?.rate, adaRate?.rate]);

  // Calculate total PEPM for each gender
  const maleTotalPepm = useMemo(() => {
    const fmla = maleRates.fmlaRate?.rate ?? 0;
    const ada = maleRates.adaRate?.rate ?? 0;
    return fmla + ada;
  }, [maleRates.fmlaRate?.rate, maleRates.adaRate?.rate]);

  const femaleTotalPepm = useMemo(() => {
    const fmla = femaleRates.fmlaRate?.rate ?? 0;
    const ada = femaleRates.adaRate?.rate ?? 0;
    return fmla + ada;
  }, [femaleRates.fmlaRate?.rate, femaleRates.adaRate?.rate]);

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  const mutateRate = useMemo(
    () =>
      (params: FmlaRateUpdateParams): void => {
        rateMutation.mutate(params, {
          onSuccess: () => onRateSaved(),
        });
      },
    [onRateSaved, rateMutation],
  );

  // Create handler context
  const handlerCtx = useMemo(
    (): FmlaHandlerContext => ({
      axiosInstance,
      scheduleId: rateSchedule?.id,
      planId: plan.id,
      mutate: mutateRate,
    }),
    [axiosInstance, rateSchedule?.id, plan.id, mutateRate],
  );

  // Single column commit handlers using factory functions
  const handleW2EmployeesCommit = useMemo(
    () => createLivesHandler(handlerCtx, fmlaRate),
    [handlerCtx, fmlaRate],
  );

  const handleFmlaPepmCommit = useMemo(
    () => createFmlaRateHandler(handlerCtx, fmlaRate, adaRate, totalRate),
    [handlerCtx, fmlaRate, adaRate, totalRate],
  );

  const handleAdaPepmCommit = useMemo(
    () => createFmlaRateHandler(handlerCtx, adaRate, fmlaRate, totalRate),
    [handlerCtx, adaRate, fmlaRate, totalRate],
  );

  // Gender-split commit handlers using factory functions
  const handleMaleW2Commit = useMemo(
    () => createLivesHandler(handlerCtx, maleRates.fmlaRate),
    [handlerCtx, maleRates.fmlaRate],
  );

  const handleMaleFmlaCommit = useMemo(
    () =>
      createFmlaRateHandler(
        handlerCtx,
        maleRates.fmlaRate,
        maleRates.adaRate,
        maleRates.totalRate,
      ),
    [handlerCtx, maleRates.fmlaRate, maleRates.adaRate, maleRates.totalRate],
  );

  const handleMaleAdaCommit = useMemo(
    () =>
      createFmlaRateHandler(
        handlerCtx,
        maleRates.adaRate,
        maleRates.fmlaRate,
        maleRates.totalRate,
      ),
    [handlerCtx, maleRates.adaRate, maleRates.fmlaRate, maleRates.totalRate],
  );

  const handleFemaleW2Commit = useMemo(
    () => createLivesHandler(handlerCtx, femaleRates.fmlaRate),
    [handlerCtx, femaleRates.fmlaRate],
  );

  const handleFemaleFmlaCommit = useMemo(
    () =>
      createFmlaRateHandler(
        handlerCtx,
        femaleRates.fmlaRate,
        femaleRates.adaRate,
        femaleRates.totalRate,
      ),
    [
      handlerCtx,
      femaleRates.fmlaRate,
      femaleRates.adaRate,
      femaleRates.totalRate,
    ],
  );

  const handleFemaleAdaCommit = useMemo(
    () =>
      createFmlaRateHandler(
        handlerCtx,
        femaleRates.adaRate,
        femaleRates.fmlaRate,
        femaleRates.totalRate,
      ),
    [
      handlerCtx,
      femaleRates.adaRate,
      femaleRates.fmlaRate,
      femaleRates.totalRate,
    ],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rateSchedule) {
    return (
      <Box sx={{ p: 1, fontSize: "13px", color: "text.secondary" }}>
        No rate schedule
      </Box>
    );
  }

  // Gender-split layout
  if (hasGender) {
    return (
      <FmlaGenderSplit
        planId={plan.id}
        maleRates={maleRates}
        femaleRates={femaleRates}
        maleTotalPepm={maleTotalPepm}
        femaleTotalPepm={femaleTotalPepm}
        onMaleW2Commit={handleMaleW2Commit}
        onMaleFmlaCommit={handleMaleFmlaCommit}
        onMaleAdaCommit={handleMaleAdaCommit}
        onFemaleW2Commit={handleFemaleW2Commit}
        onFemaleFmlaCommit={handleFemaleFmlaCommit}
        onFemaleAdaCommit={handleFemaleAdaCommit}
        readOnly={readOnly}
        renewal={renewal}
        fieldPathOf={fieldPathOf}
      />
    );
  }

  // Single-column layout (no gender split)
  return (
    <FmlaSingleColumn
      planId={plan.id}
      fmlaRate={fmlaRate}
      adaRate={adaRate}
      totalPepm={totalPepm}
      rateScheduleNumberOfLives={rateSchedule?.numberOfLives}
      onW2EmployeesCommit={handleW2EmployeesCommit}
      onFmlaPepmCommit={handleFmlaPepmCommit}
      onAdaPepmCommit={handleAdaPepmCommit}
      readOnly={readOnly}
      renewal={renewal}
      fieldPathOf={fieldPathOf}
    />
  );
};

export default FmlaPepmRenderer;
